import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ArchiveRow from "@/components/ArchiveRow";
import { computeStats, groupByMonth } from "@/data/archives";
import { useLang } from "@/contexts/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StatCard = ({ label, value, testid }) => (
  <div data-testid={testid} className="bg-black border border-white/10 p-6 md:p-8 relative overflow-hidden group">
    <div className="text-[10px] tracking-[0.3em] text-white/50 mb-3">{label}</div>
    <div className="font-display text-4xl md:text-5xl tracking-tight">{value}</div>
    <div className="absolute bottom-0 left-0 h-[2px] w-0 bg-[var(--hk-red)] group-hover:w-full transition-[width] duration-500" />
  </div>
);

const ArchivesPage = () => {
  const { t } = useLang();
  const [rows, setRows] = useState([]);
  const [siteStats, setSiteStats] = useState(null);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    let ignore = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/archive?limit=500`);
        if (!ignore) setRows(data);
      } catch (e) {
        console.error("archive fetch failed", e);
      }
      try {
        const { data } = await axios.get(`${API}/settings/stats`);
        if (!ignore) setSiteStats(data);
      } catch (e) {
        console.error("site stats fetch failed", e);
      }
      try {
        const { data } = await axios.get(`${API}/settings/archive-notice`);
        if (!ignore) setNotice(data);
      } catch (e) {
        console.error("archive notice fetch failed", e);
      }
    })();
    return () => { ignore = true; };
  }, []);

  const computed = computeStats(rows);
  const s = siteStats || {
    total_tips: computed.total,
    wins: computed.wins,
    losses: computed.losses,
    win_rate: computed.winRate,
  };
  const months = groupByMonth(rows);

  return (
    <div data-testid="archives-page" className="grain">
      <Header />
      <main className="pt-28 md:pt-36 pb-24">
        <div className="container-hk">
          <Link
            to="/"
            data-testid="archives-back-home"
            className="inline-block mb-8 text-[10px] font-semibold tracking-[0.3em] text-white/60 hover:text-[var(--hk-red)] transition-colors"
          >
            ← {t.nav.home}
          </Link>

          <div className="text-[10px] md:text-xs tracking-[0.35em] text-[var(--hk-red)] font-bold mb-3">
            {t.archive.overline}
          </div>
          <h1 className="font-display text-4xl md:text-6xl lg:text-7xl uppercase leading-[0.95] tracking-tight mb-10">
            {t.archive.title}
          </h1>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 mb-16">
            <StatCard label={t.stats.total_tips} value={s.total_tips} testid="arch-stat-total" />
            <StatCard label={t.archive.win + "S"} value={s.wins} testid="arch-stat-wins" />
            <StatCard label={t.archive.loss + "ES"} value={s.losses} testid="arch-stat-losses" />
            <StatCard label="WIN RATE" value={`${s.win_rate}%`} testid="arch-stat-winrate" />
          </div>

          {notice?.enabled && notice.text && (
            <p
              data-testid="archive-notice"
              className="-mt-8 mb-8 text-center text-sm md:text-base font-semibold text-[var(--hk-red)] leading-relaxed max-w-3xl mx-auto"
            >
              {notice.text}
            </p>
          )}

          {months.map((m) => (
            <motion.section
              key={m.key}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5 }}
              className="mb-14"
              data-testid={`arch-month-${m.key}`}
            >
              <div className="flex items-center gap-4 mb-4">
                <h2 className="font-display text-2xl md:text-3xl uppercase tracking-tight">{m.label}</h2>
                <div className="flex-1 hr-dot" />
                <span className="text-xs text-white/50 font-mono">{m.rows.length} days</span>
              </div>
              <div className="border border-white/10">
                {m.rows.map((row) => (
                  <ArchiveRow key={row.id || row.iso} row={row} />
                ))}
              </div>
            </motion.section>
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ArchivesPage;
